// src/pages/DonatePage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Wallet, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';

const DonatePage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, openAuthModal } = useAuthContext();
  const [donationAmount, setDonationAmount] = useState('');
  const [isDonating, setIsDonating] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' | 'error' | ''

  // پرداخت از طریق کیف پول
  const handleWalletDonation = async () => {
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }
    
    const amount = parseFloat(donationAmount);
    if (!donationAmount || amount <= 0) {
      setMessage('لطفاً مبلغ معتبر وارد کنید.');
      setMessageType('error');
      return;
    }

    setIsDonating(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await apiClient.post('/wallet/donate/', {
        amount: amount
      });

      // ✅ بررسی پاسخ از بک‌اند (بر اساس ساختار DonationView)
      if (response.data.detail || response.data.success) {
        const successMessage = response.data.detail || `✅ کمک مالی ${donationAmount.toLocaleString()} تومانی با موفقیت از کیف پول شما کسر شد.`;
        setMessage(successMessage);
        setMessageType('success');
        setDonationAmount('');
        
        // به‌روزرسانی موجودی کیف پول (می‌توانید event یا context را صدا بزنید)
        // می‌توانید یک event منتشر کنید تا سایر کامپوننت‌ها موجودی را به‌روز کنند
        window.dispatchEvent(new CustomEvent('wallet-updated'));
      } else {
        throw new Error(response.data.error || 'خطا در پرداخت');
      }
    } catch (err) {
      console.error('Donation error:', err);
      const errorMsg = err.response?.data?.error || 'خطا در انجام کمک مالی. موجودی کیف پول کافی نیست؟';
      setMessage(errorMsg);
      setMessageType('error');
    } finally {
      setIsDonating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 rtl pb-16">
      {/* محتوا */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-center mb-6">
            <Heart size={30} className="text-red-600" />
          </div>
          
          {/* تیتر */}
          <h2 className="text-lg font-bold text-center text-purple-800 mb-2">
            کمک به توسعه وب اپلیکیشن فرزانه
          </h2>
          <p className="text-xs text-gray-600 text-justify leading-relaxed mb-6">
            با کمک مالی خود، ما را در توسعه و بهبود اپلیکیشن فرزانه یاری کنید. 
            هر کمک شما، حتی کوچک، در پیشرفت این مسیر ارزشمند است.
          </p>

          {/* بخش کمک از طریق کیف پول */}
          <div className="border rounded-lg p-4 mb-5 bg-purple-50 border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <Wallet size={24} className="text-purple-700" />
              <h3 className="font-bold text-gray-800">کمک مالی از طریق کیف پول</h3>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">مبلغ به تومان</label>
                <input
                  type="number"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  placeholder="مثال: 50000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isDonating}
                />
              </div>
              <button
                onClick={handleWalletDonation}
                disabled={isDonating}
                className="bg-purple-700 text-white px-6 py-2.5 rounded-lg hover:bg-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium min-w-[140px] justify-center"
              >
                {isDonating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    در حال پرداخت...
                  </>
                ) : (
                  'کمک با کیف پول'
                )}
              </button>
            </div>

            {/* نمایش پیام */}
            {message && (
              <div className={`mt-3 text-sm text-center p-3 rounded-lg flex items-center gap-2 justify-center ${
                messageType === 'success' 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}>
                {messageType === 'success' ? (
                  <Check size={18} className="text-green-600" />
                ) : (
                  <AlertCircle size={18} className="text-red-600" />
                )}
                <span>{message}</span>
              </div>
            )}

            {/* نمایش موجودی کیف پول */}
            {isLoggedIn && (
              <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                <Wallet size={14} />
                <span>موجودی کیف پول شما قابل مشاهده است.</span>
              </div>
            )}
          </div>

          {/* بخش تشکر */}
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 text-center mt-2 border border-purple-200">
            <p className="text-purple-800 text-sm font-medium">با تشکر از همراهی شما</p>
            <p className="text-xs text-gray-600 mt-2">
              کمک‌های مالی شما، صرف توسعه نرم‌افزار، خرید سرور و تولید محتوای باکیفیت‌تر خواهد شد.
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link 
              to="/profile" 
              className="block w-full text-center bg-gray-200 text-gray-700 py-2.5 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              بازگشت به پروفایل
            </Link>
            <Link 
              to="/profile/wallet" 
              className="block w-full text-center bg-purple-100 text-purple-700 py-2.5 rounded-lg hover:bg-purple-200 transition font-medium"
            >
              مشاهده کیف پول
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;